import React, { createContext, useContext, useState, useCallback } from "react";
import { privateAxios } from "../utils/axios";
import AuthContext from "../AuthContext";
import toast from "react-hot-toast";

const PermissionContext = createContext();

export const usePermissionContext = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error(
      "usePermissionContext must be used within a PermissionProvider"
    );
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [permissionUpdateTrigger, setPermissionUpdateTrigger] = useState(0);
  const auth = useContext(AuthContext);

  // Refresh permissions for the current user
  const refreshUserPermissions = useCallback(async () => {
    if (!auth.user) return null;

    setIsRefreshing(true);
    try {
      const response = await privateAxios.get("/auth/me");
      if (response.data.success) {
        // Update localStorage with fresh user data
        localStorage.setItem("user", JSON.stringify(response.data.data));

        // Trigger re-render of components that use permissions
        setPermissionUpdateTrigger((prev) => prev + 1);

        return response.data.data;
      }
    } catch (error) {
      console.error("Error refreshing permissions:", error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [auth.user]);

  // Trigger permission refresh after user update
  const onUserUpdated = useCallback(
    async (updatedUserId) => {
      try {
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

        // If the updated user is the current user, refresh their permissions
        if (currentUser._id === updatedUserId) {
          await refreshUserPermissions();
          toast.success("Your permissions have been updated!", {
            duration: 3000,
            icon: "🔄",
          });
        }
      } catch (error) {
        console.error("Error handling user update:", error);
      }
    },
    [refreshUserPermissions]
  );

  const value = {
    refreshUserPermissions,
    onUserUpdated,
    isRefreshing,
    permissionUpdateTrigger, // This will cause re-renders when permissions change
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
